import { Injectable } from "@angular/core";
import { Actions, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import { ActionWithPayload, batchActions } from "@obg/ngrx-utils";
import { Log, LoggingFactory } from "@obg/ngx.global.evo";
import { isFunction, keys } from "lodash-es";
import { Observable } from "rxjs";
import { filter, map, tap, withLatestFrom } from "rxjs/operators";

import { CoreState } from "../state";

import { MessageAction } from "./message.action";
import { Message, MessageFilter, MessageScopeType, MessageType } from "./message.model";
import { MessageSelector } from "./message.selector";
import { toMessage } from "./message.util";

@Injectable({
	providedIn: "root",
})
export class MessageService {
	logger: Log;

	constructor(
		private actions$: Actions,
		private store: Store<CoreState>,
		private action: MessageAction,
		private selector: MessageSelector,
		loggingFactory: LoggingFactory
	) {
		this.logger = loggingFactory.get("MessageService");
	}

	map$(messageByAction: Record<string, Message | ((state: CoreState) => Message | null)>): Observable<Action> {
		return this.actions$.pipe(
			ofType(...this.getActions(messageByAction)),
			map((action) => messageByAction[action.type]),
			withLatestFrom(this.store),
			map(([message, store]) => (isFunction(message) ? message(store) : message)),
			filter((message) => !!message),
			map((message) => this.action.add(message!))
		);
	}

	mapErrorByContext$(
		contextByAction: Record<string, string>,
		scope = MessageScopeType.Local,
		type = MessageType.Error
	): Observable<any> {
		return this.actions$.pipe(
			ofType(...this.getActions(contextByAction)),
			tap((action: ActionWithPayload) => {
				// log fatal errors, as exceptions originating from service implementation might not be logged anywhere else
				// todo: rethrow app exceptions instead OBGB-10687
				if (type === MessageType.Fatal) {
					this.logger.error(
						"mapErrorByContext$",
						`fatal error in ${contextByAction[action.type]}`,
						action.payload
					);
				}
			}),
			map((action: ActionWithPayload) =>
				this.action.add(toMessage(action.payload, contextByAction[action.type], scope, type))
			)
		);
	}

	mapClearByContext$(contextByAction: Record<string, string>): Observable<Action> {
		return this.actions$.pipe(
			ofType(...this.getActions(contextByAction)),
			withLatestFrom(this.store),
			filter(([action, state]) => this.selector.has({ context: contextByAction[action.type] })(state)),
			map(([action]) => this.action.clearByContext(contextByAction[action.type]))
		);
	}

	/**
	 * Returns the messages based on the given filter and removes them from the store.
	 */
	consume$(messageFilter: MessageFilter): Observable<Message[]> {
		return this.store.pipe(
			this.selector.getAll$(messageFilter),
			filter((messages: Message[]) => !!messages.length),
			tap((messages: Message[]) => {
				if (messageFilter?.context) {
					this.store.dispatch(
						Array.isArray(messageFilter.context)
							? batchActions(
									...messageFilter.context.map((context) => this.action.clearByContext(context))
								)
							: this.action.clearByContext(messageFilter.context)
					);
					return;
				}
				this.store.dispatch(
					batchActions(...messages.map((message) => this.action.clearByCode(message.messageCode)))
				);
			})
		);
	}

	private getActions(
		contextByAction: Record<string, string | Message | ((state: CoreState) => Message | null)>
	): string[] {
		return keys(contextByAction);
	}
}
